import type { AuditLogEntry } from '@shared/types';
import { api } from './api';

/**
 * Admin audit-log API client. Wraps GET /admin/audit, which supports filters
 * by actor, target type/id, and a time range. The endpoint returns the usual
 * admin pagination shape `{ data, page, pageSize, total, hasMore }`.
 *
 * Separate from the user detail's inline audit preview (see
 * `services/users.ts::getUserAudit`) — that one is hard-coded to
 * `targetType=user` + `pageSize=5`; this one is the generic viewer used by
 * the `/audit` page.
 */

export interface ListAuditParams {
  actorId?: string;
  targetType?: string;
  targetId?: string;
  /** ISO timestamp, inclusive lower bound. */
  from?: string;
  /** ISO timestamp, inclusive upper bound. */
  to?: string;
  page?: number;
  pageSize?: number;
}

export interface AuditListResponse {
  data: AuditLogEntry[];
  page: number;
  pageSize: number;
  /** TRUE match count — never clamped. */
  total: number;
  hasMore: boolean;
  /**
   * Highest page the server will serve. Paginate on THIS, not on
   * `Math.ceil(total / pageSize)`: the audit read unions two databases and
   * merges rather than using skip(), so it 400s past a bounded window.
   * Optional so an older server response still parses.
   */
  maxPage?: number;
}

function toQuery(params: Record<string, unknown>): string {
  const parts: string[] = [];
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  }
  return parts.length > 0 ? `?${parts.join('&')}` : '';
}

export async function listAudit(
  params: ListAuditParams = {},
): Promise<AuditListResponse> {
  const qs = toQuery({
    actorId: params.actorId,
    targetType: params.targetType,
    targetId: params.targetId,
    from: params.from,
    to: params.to,
    page: params.page ?? 1,
    pageSize: params.pageSize ?? 50,
  });
  return api<AuditListResponse>(`/admin/audit${qs}`);
}
